import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
} from "recharts";
import { useWavesurfer } from "@wavesurfer/react";
//@ts-ignore
import Spectrogram from "wavesurfer.js/dist/plugins/spectrogram.esm.js";
import { ReactComponent as Play } from "../assets/icons/play.svg";
import { ReactComponent as Pause } from "../assets/icons/pause.svg";
import BackArrow from "../assets/images/back-arrow.svg";
import CardiovascularObservationResult from "./CardiovascularObservationResult";
import { Tab } from "@headlessui/react";
import { constants } from "../utils/ranges";
import { useAuth } from "../utils/auth";

import { ReactComponent as Download } from "../assets/icons/Download.svg";
import CarePlixLogo from "../assets/images/CarePlixLogo.png";
import CoverImage from "../assets/images/coverImage.png";
import BodyVitalReportImage from "../assets/images/BodyVitalReportImage.png";
import Heart from "../assets/images/heart.png";
import OxygenSaturation from "../assets/images/OxygenSaturation.png";
import HeartRate from "../assets/images/HeartRate.png";
import RespirationRate from "../assets/images/RespirationRate.png";
import HeartRateMax from "../assets/images/HeartRateMax.png";
import PNN50 from "../assets/images/PNN50.png";
import CardiacOutput from "../assets/images/CardiacOutput.png";
import MeanArterialPressure from "../assets/images/MeanArterialPressure.png";
import HeartUtilization from "../assets/images/HeartUtilization.png";
import HeartRateReserve from "../assets/images/HeartRateReserve.png";
import TargetHeartRateRange from "../assets/images/TargetHeartRateRange.png";
import VO2Max from "../assets/images/VO2Max.png";
import TotalBodyWater from "../assets/images/TotalBodyWater.png";
import TotalBodyFat from "../assets/images/TotalBodyFat.png";
import BloodVolume from "../assets/images/BloodVolume.png";
import BodyWater from "../assets/images/BodyWater.png";
import BloodPressure from "../assets/images/BloodPressure.png";
import Physiological from "../assets/images/Physiological.png";
import BodyWeight from "../assets/images/BodyWeight.png";
import stress from "../assets/images/stress.png";

import {
  Font,
  Document,
  Page,
  StyleSheet,
  View,
  Text,
  Image,
  PDFDownloadLink,
} from "@react-pdf/renderer";

Font.register({
  family: "Poppins",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLDz8V1tvFP-KUEg.ttf",
      fontWeight: "light",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiEyp8kv8JHgFVrFJDUc1NECPY.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLGT9V1tvFP-KUEg.ttf",
      fontWeight: "medium",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLEj6V1tvFP-KUEg.ttf",
      fontWeight: "semibold",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLCz7V1tvFP-KUEg.ttf",
      fontWeight: "bold",
    },
  ],
});

const PDFReport = ({
  name = "",
  age = 0,
  gender = "",
  height = 0,
  weight = 0,
  result = {
    timestamp: new Date(),
    posture: "resting",
    bp_sys: 0,
    bp_dia: 0,
    resp_rate: 0,
    oxy_sat_prcnt: 0,
    heart_rate: 0,
    bmi: 0,
    sdnn: 0,
    rmssd: 0,
    pNN50_per: 0,
    cardiac_out: 0.0,
    map: 0,
    heart_utilized: 0,
    HRMax: 0,
    HRR: 0,
    THRR: 0,
    vo2max: 0.0,
    bloodvolume: 0.0,
    tbw: 0.0,
    tbwp: 0.0,
    bodyfat: 0.0,
    weight: 0.0,
    overallScore: 0,
    pulmoScore: "",
    tbf: 0.0,
    emm: 0.0,
    fitnessLevel: "",
    prq: 0.0,
    fev1fvcRatio: 0.0,
    isGood: "",
    hba1c: 0,
  },
}) => {
  const styles = StyleSheet.create({
    page: {
      backgroundColor: "#fff",
      flexDirection: "column",
      alignItems: "stretch",
      justifyContent: "space-between",
    },
    header: {
      backgroundColor: "#f7f7f7",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 24,
      paddingVertical: 16,
      borderBottom: 1,
      borderBottomColor: "#D3D3D3",
    },
    contentContainer: { flex: 1, paddingVertical: 12, paddingHorizontal: 24 },
    rowContainer: { flexDirection: "row", justifyContent: "space-between" },
    largeText: { fontFamily: "Poppins", fontSize: 15, fontWeight: "bold" },
    normalText: { fontFamily: "Poppins", fontSize: 12, fontWeight: "semibold" },
    smallText: { fontFamily: "Poppins", fontSize: 10 },
    footer: {
      height: 30,
      backgroundColor: "#192852",
      width: "100%",
    },
    coverPage: {
      backgroundColor: "#000068",
      padding: 0,
      height: "100%",
    },
    // coverImageContainer: {
    //   width: "100%",
    //   height: "80%",
    //   overflow: "hidden",
    //   position: "relative",
    //   backgroundColor: "#000068",
    // },
    coverImageContainer: {
      width: "100%",
      height: "100%",
      backgroundColor: "#000068",
      alignItems: "center", // center horizontally
      justifyContent: "center", // center vertically
    },

    // coverImage: {
    //   width: "100%",
    //   height: "100%",
    //   objectFit: "cover",
    //   justifyContent: "center",
    // },
    coverImage: {
      width: "100%", // make smaller (set your own size)
      height: "80%",
      resizeMode: "contain", // prevent stretching
    },
    coverLogo: {
      position: "absolute",
      top: 20,
      left: 20,
      width: 220,
      height: 75,
      objectFit: "contain",
      backgroundColor: "#FFF",
      borderRadius: 15,
    },
    coverTitleArea: {
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: "#F4F7F8",
      paddingVertical: 10,
      paddingHorizontal: 64,
      borderTopLeftRadius: 64,
      borderTopRightRadius: 64,
    },
    reportTitle: {
      fontSize: 24,
      fontWeight: "normal",
      textAlign: "center",
      marginBottom: 4,
      fontFamily: "Poppins",
    },
    reportSubtitle: {
      fontSize: 36,
      fontWeight: "bold",
      textAlign: "center",
      color: "#00ABFF",
      fontFamily: "Poppins",
    },
    headerText: {
      fontFamily: "Poppins",
      fontSize: 10,
      color: "#666",
    },
    circleContainer: {
      width: 130,
      height: 130,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#E9F3FF",
      borderRadius: 25,
      marginRight: 15,
      paddingVertical: 5,
      paddingHorizontal: 5,
    },

    titleText: {
      fontFamily: "Poppins",
      fontSize: 13,
      fontWeight: "600",
      color: "#1A1A1A",
      textAlign: "center",
      marginBottom: 8,
    },

    statusBox: {
      width: 80,
      height: 50,
      backgroundColor: "#FFFFFF",
      borderRadius: 30,
      justifyContent: "center",
      alignItems: "center",
      shadowColor: "#000",
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },

    scoreText: {
      fontFamily: "Poppins",
      fontSize: 10,
      fontWeight: "400",
      color: "#5A5A5A",
      textAlign: "center",
    },
    tableContainer: {
      marginTop: 10,
      borderRadius: 10,
      overflow: "hidden",
    },
    tableHeaderRow: {
      flexDirection: "row",
      backgroundColor: "#2E5894",
      color: "#fff",
      paddingVertical: 5,
      paddingHorizontal: 12,
    },
    tableHeaderCell: {
      fontFamily: "Poppins",
      fontSize: 12,
      fontWeight: "bold",
      color: "#fff",
      flex: 1,
    },
    tableRow: {
      flexDirection: "row",
      backgroundColor: "#fff",
      borderBottom: "1px solid #eee",
      paddingVertical: 8,
      paddingHorizontal: 12,
      alignItems: "center",
    },
    tableCell: {
      fontFamily: "Poppins",
      fontSize: 12,
      color: "#333",
      flex: 1,
    },
    adjustedMuscleMassRow: {
      flexDirection: "row",
      backgroundColor: "#2E5894",
      color: "#fff",
      paddingVertical: 4,
      paddingHorizontal: 12,
      borderRadius: 10,
    },
    adjustedCell: {
      fontFamily: "Poppins",
      fontSize: 12,
      fontWeight: "bold",
      color: "#fff",
    },
    disclaimerContainer: {
      marginTop: 30,
      padding: 15,
      borderRadius: 10,
      backgroundColor: "#F4F7F8",
      border: "1px solid #eee",
    },
    disclaimerText: {
      fontFamily: "Poppins",
      fontSize: 9,
      fontWeight: "normal",
      color: "#666",
      textAlign: "justify",
    },
    bodyVitalsContainer: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: "#2E5894",
      borderRadius: 12,
      paddingHorizontal: 10,
      paddingVertical: 8,
      width: "100%",
    },
    textContainer: {
      flex: 1,
    },
    bodyVitalstitle: {
      fontSize: 16,
      fontWeight: 700,
      color: "#FFFFFF",
      marginBottom: 4,
    },
    highlight: {
      color: "#4BA3F2",
    },
    bodyVitalSubtitle: {
      fontSize: 10,
      color: "#DADADA",
    },
    bodyVitalsImage: {
      width: 70,
      height: 40,
      objectFit: "contain",
    },
    stressBox: {
      marginTop: 15,
      padding: 15,
      borderRadius: 10,
      backgroundColor: "#f7f7f7",
      border: "1px solid #eee",
    },
  });

  return (
    <Document
      title="CarePlix Report"
      subject="Report generated from CarePlix Vitals Scan"
      author="CarePlix"
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.coverPage}>
          <View style={styles.coverImageContainer}>
            <Image style={styles.coverImage} src={CoverImage} />
            <Image style={styles.coverLogo} src={CarePlixLogo} />
          </View>
          <View style={styles.coverTitleArea}>
            <Text style={styles.reportSubtitle}>Digital Health</Text>
            <Text style={styles.reportTitle}>
              Pulmonary Health & Vitals Report
            </Text>
          </View>
        </View>
      </Page>
      <Page style={styles.page}>
        <View fixed style={styles.header}>
          <View>
            <Text
              style={{ color: "#00ABFF", fontWeight: "bold", fontSize: 18 }}
            >
              Digital Health
            </Text>
            <Text style={{ fontWeight: "normal", fontSize: 20 }}>
              Pulmonary Health & Vitals Report
            </Text>
          </View>
          <Text style={styles.headerText}>
            Date & Time:{" "}
            {new Date(result.timestamp).toLocaleDateString("en-GB")}
            {", "}
            {new Date(result.timestamp).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })}
          </Text>
        </View>
        <View style={styles.contentContainer}>
          <View style={styles.rowContainer}>
            <View style={{ flex: 1.5, paddingRight: 40 }}>
              {[
                { label: "Name", value: name },
                { label: "Age", value: `${age} years` },
                { label: "Gender", value: gender },
                { label: "Height", value: `${height} cms` },
                { label: "Weight", value: `${weight} kgs` },
              ].map(({ label, value }) => (
                <Text
                  key={label.toLowerCase().replaceAll(" ", "-")}
                  style={[
                    styles.normalText,
                    {
                      fontWeight: "normal",
                      marginTop: label === "Name" ? 0 : 8,
                      borderBottom: 1,
                      borderBottomColor: "#E7E7E7",
                    },
                  ]}
                >
                  <Text style={{ fontWeight: "semibold", minWidth: 60 }}>
                    {label} :
                  </Text>
                  <Text
                    style={{
                      textTransform: label === "Name" ? "capitalize" : "none",
                    }}
                  >
                    {` ${value}`}
                  </Text>
                </Text>
              ))}
            </View>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "center",
                marginTop: 10,
              }}
            >
              {[
                {
                  title: "Overall Health Score",
                  score: `${result.overallScore}%`,
                },
                {
                  title: "Pulmonary Health Score",
                  score: `${Math.round(result.pulmoScore)}%`,
                },
              ].map((item) => (
                <View style={styles.circleContainer} key={item.title}>
                  <Text style={styles.titleText}>{item.title}</Text>
                  <View style={styles.statusBox}>
                    <Text style={styles.scoreText}>{item.score}</Text>
                    {/* <Text style={styles.subtext}>{item.status}</Text> */}
                  </View>
                </View>
              ))}
            </View>
          </View>

          <Text
            style={{
              fontWeight: "semibold",
              fontSize: 12,
              marginTop: 20,
              marginBottom: 10,
            }}
          >
            Your Pulmomary Observation
          </Text>

          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2.5 }]}>
                Parameter
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1 }]}>Status</Text>
            </View>

            {[
              {
                label: "Predicted FEV1/FVC",
                value: result?.fev1fvcRatio,
                status: {
                  label:
                    result?.fev1fvcRatio >= 0.7
                      ? "Normal"
                      : "Low – Possible Obstruction",
                  color: result?.fev1fvcRatio >= 0.7 ? "#59D341" : "#FF2B2B",
                },
              },
              {
                label: "Obstructive Airway Disease (OAD)",
                value: "",
                status: {
                  label: result.isGood ? "Not Present" : "Present",
                  color: result.isGood ? "#59D341" : "#FF2B2B",
                },
              },
              (() => {
                const prq = parseFloat(result.prq);
                let status = {};

                if (prq >= 1.6 && prq <= 2.2) {
                  status = { label: "Normal", color: "#59D341" };
                } else if (prq > 2.2) {
                  status = {
                    label: "High",
                    color: "#FF8C00",
                  };
                } else if (prq < 1.4) {
                  status = {
                    label: "Low",
                    color: "#FF2B2B",
                  };
                } else {
                  status = { label: "Normal", color: "#FF8C00" };
                }

                return {
                  label: "Pulse-Respiration Quotient (PRQ)",
                  value: result.prq,
                  status,
                };
              })(),
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text style={[styles.tableCell, { flex: 2.5 }]}>
                  {item.label}
                </Text>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>
                  {item.value}
                </Text>
                <View
                  style={[
                    styles.smallText,

                    styles.tableCell,
                    {
                      alignItems: "center",
                      fontFamily: "Poppins",
                      fontSize: 10,
                      flex: 1,
                    },
                  ]}
                >
                  <Text style={{ color: item.status?.color || "#000" }}>
                    {item.status.label}
                  </Text>
                </View>
              </View>
            ))}
          </View>

          <View style={styles.disclaimerContainer}>
            <Text
              style={[
                styles.largeText,
                { marginTop: 2, marginBottom: 8, fontSize: 10 },
              ]}
            >
              Disclaimer
            </Text>
            <Text style={styles.disclaimerText}>
              For <Text style={styles.boldText}>Investigational Use Only</Text>.
              This scan is not a substitute for the clinical judgement of a
              health care professional. This scan is intended to improve you
              awareness of general wellness. This scan does not diagnose, treat,
              mitigate or prevent any disease, symptom, disorder or abnormal
              physical state. Consult with a healthcare professional or
              emergency services if you believe you may have a medical issue.
            </Text>
          </View>
        </View>
        <View style={styles.contentContainer} break>
          <View style={styles.bodyVitalsContainer}>
            <View style={styles.textContainer}>
              <Text style={styles.bodyVitalstitle}>
                Key Body <Text style={styles.highlight}>Vitals</Text>
              </Text>
              <Text style={styles.bodyVitalSubtitle}>
                Monitoring essential health metrics for optimal wellness
              </Text>
            </View>

            <Image src={BodyVitalReportImage} style={styles.bodyVitalsImage} />
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                Key Body Vitals
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                Normal Range
              </Text>
            </View>
            {[
              {
                label: "Blood Pressure",
                value: `${result.bp_sys}/${result.bp_dia} mmHg`,
                status: (() => {
                  const sysStatus = constants[
                    "Blood Pressure"
                  ].ranges[0].reduce((p, c) =>
                    result.bp_sys >= c.point ? c : p
                  ) || { label: "Unknown", color: "#000" };

                  const diaStatus = constants[
                    "Blood Pressure"
                  ].ranges[1].reduce((p, c) =>
                    result.bp_dia >= c.point ? c : p
                  ) || { label: "Unknown", color: "#000" };

                  const sysIndex = constants[
                    "Blood Pressure"
                  ].ranges[0].findIndex((r) => r.label === sysStatus.label);
                  const diaIndex = constants[
                    "Blood Pressure"
                  ].ranges[1].findIndex((r) => r.label === diaStatus.label);

                  return sysIndex > diaIndex ? sysStatus : diaStatus;
                })(),
                range: "90–120/60–80 mmHg",
              },

              {
                label: "Heart Rate",
                value: `${result.heart_rate} BPM`,
                status: constants["Heart Rate"].ranges["resting"]?.reduce?.(
                  (p, c) => (result.heart_rate >= c.point ? c : p)
                ) || { label: "Unknown", color: "#000" },
                range: "60 - 100 BPM",
              },
              {
                label: "Respiration Rate",
                value: `${result.resp_rate} breaths/min`,
                status: constants["Respiration Rate"].ranges.reduce((p, c) =>
                  result.resp_rate >= c.point ? c : p
                ) || { label: "Unknown", color: "#000" },
                range: "12 - 20/min",
              },
              {
                label: "Oxygen Saturation",
                value: `${result.oxy_sat_prcnt} %`,
                status: constants["Oxygen Saturation"].ranges.reduce((p, c) =>
                  result.oxy_sat_prcnt >= c.point ? c : p
                ) || { label: "Unknown", color: "#000" },
                range: "95 - 100 %",
              },
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 2, fontWeight: "semibold" },
                  ]}
                >
                  {item.label}
                </Text>
                <View style={{ flex: 1.5, flexDirection: "column" }}>
                  <Text
                    style={[styles.tableCell, { padding: 0, borderBottom: 0 }]}
                  >
                    {item.value}
                  </Text>
                  <Text
                    style={[
                      styles.smallText,
                      {
                        fontFamily: "Poppins",
                        fontSize: 8,
                        color: item.status?.color || "#000",
                        fontWeight: "bold",
                        marginTop: 20,
                      },
                    ]}
                  >
                    {item.status?.label || ""}
                  </Text>
                </View>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>
                  {item.range}
                </Text>
              </View>
            ))}
          </View>

          <Text style={[styles.largeText, { marginTop: 15, marginBottom: 10 }]}>
            Health Parameters Description
          </Text>
          {[
            {
              icon: BloodPressure,
              title: "Blood Pressure",
              desc: "Blood pressure is a measure of the force that the heart uses to pump blood around the body. Blood pressure is measured in millimeters of mercury (mmHg) and is given as 2 figures:\n\u2022 Systolic pressure - the pressure when your heart pushes blood out\n\u2022 Diastolic pressure - the pressure when your heart rests between beats",
            },
            {
              icon: HeartRate,
              title: "Heart Rate",
              desc: "The heart rate is the number of times heart beats in a minute (bp 4). A normal resting heart rate for adults ranges from 60 to 100 beats per minute. A person's resting heart rate is the heart rate during physical activity and exercise. A normal resting heart rate for adults ranges from 60 to 100 bpm.",
            },
            {
              icon: RespirationRate,
              title: "Respiration Rate",
              desc: "The respiration rate is the number of breaths taken in a minute. The rate is usually measured when a person is at rest and simply involves counting the number of breaths for one minute by counting how many times the chest rises.",
            },
            {
              icon: OxygenSaturation,
              title: "Oxygen Saturation",
              desc: "Oxygen saturation (also known as SpO2) is the measurement used to assess the amount of oxygen being carried by the blood and delivered to body's tissues.",
            },
          ].map(({ title, desc, icon }) => (
            <View
              key={`info-${title.toLowerCase().replaceAll(" ", "-")}`}
              style={{ marginBottom: 10, flexDirection: "row" }}
              wrap={false}
            >
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 6,
                  backgroundColor: "#EAF5FF",
                  border: "1px solid #A7CDF3",
                  justifyContent: "center",
                  alignItems: "center",
                  marginRight: 8,
                }}
              >
                <Image
                  src={icon}
                  style={{
                    width: 20,
                    height: 20,
                  }}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text
                  style={[
                    styles.normalText,
                    { fontWeight: "bold", fontSize: 12, color: "#333" },
                  ]}
                >
                  {title}
                </Text>
                <Text
                  style={[
                    styles.smallText,
                    {
                      fontWeight: "light",
                      textAlign: "justify",
                      marginTop: 2,
                      color: "#666",
                    },
                  ]}
                >
                  {desc}
                </Text>
              </View>
            </View>
          ))}
        </View>

        <View style={styles.contentContainer} break>
          <View style={styles.bodyVitalsContainer}>
            <View style={styles.textContainer}>
              <Text style={styles.bodyVitalstitle}>
                Heart <Text style={styles.highlight}>Health</Text>
              </Text>
              <Text style={styles.bodyVitalSubtitle}>
                Understanding key heart health metrics.
              </Text>
            </View>

            <Image src={Heart} style={styles.bodyVitalsImage} />
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                Heart Health
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                Normal Range
              </Text>
            </View>
            {[
              {
                label: "SDNN",
                value: `${parseFloat(result.sdnn).toFixed(2)} ms`,
                range: "30 - 95 ms",
                status:
                  result.sdnn !== "--"
                    ? constants["SDNN"].ranges.reduce((p, c) =>
                        result.sdnn >= c.point ? c : p
                      )
                    : undefined,
              },
              {
                label: "RMSSD",
                value: `${parseFloat(result.rmssd).toFixed(2)} ms`,
                range: "20 - 89 ms",
                status:
                  result.rmssd !== "--"
                    ? constants["RMSSD"].ranges.reduce((p, c) =>
                        result.rmssd >= c.point ? c : p
                      )
                    : undefined,
              },
              {
                label: "PNN50",
                value: `${parseFloat(result.pNN50_per).toFixed(2)} %`,
                range: "5 - 40 %",
                status:
                  result.pNN50_per !== "--"
                    ? constants["PNN50"].ranges.reduce((p, c) =>
                        result.pNN50_per >= c.point ? c : p
                      )
                    : undefined,
              },
              {
                label: "Cardiac Output",
                value: `${parseFloat(result.cardiac_out).toFixed(2)} L/min`,
                range: "4 - 7 L/min",
                status:
                  result.cardiac_out !== "--"
                    ? constants["Cardiac Output"].ranges.reduce((p, c) =>
                        result.cardiac_out >= c.point ? c : p
                      )
                    : undefined,
              },
              {
                label: "Mean Arterial Pressure",
                value: `${result.map} mmHg`,
                range: "70 - 100 mmHg",
                status:
                  result.map !== "--"
                    ? constants["Mean Arterial Pressure"].ranges.reduce(
                        (p, c) => (result.map >= c.point ? c : p)
                      )
                    : undefined,
              },
              {
                label: "Heart Utilized",
                value: `${result.heart_utilized} %`,
                range: "41 - 70 %",
                status:
                  result.heart_utilized !== "--"
                    ? constants["Heart Utilized"].ranges.reduce((p, c) =>
                        result.heart_utilized >= c.point ? c : p
                      )
                    : undefined,
              },
              {
                label: "Heart Rate Max",
                value: `${result.HRMax} BPM`,
                range: "NA",
                status: { label: "", color: "" },
              },
              {
                label: "Heart Rate Reserve",
                value: `${result.HRR} BPM`,
                range: "NA",
                status: { label: "", color: "" },
              },
              {
                label: "Target Heart Rate Range",
                value: `${result.THRR} BPM`,
                range: "NA",
                status: { label: "", color: "" },
              },
              {
                label: "VO2 Max",
                value: `${parseFloat(result.vo2max).toFixed(2)} mL/kg/min`,
                range: `42.5 mL/kg/min (Male)\n33 mL/kg/min (Female)`,
                status: { label: "", color: "" },
              },
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 2.5, fontWeight: "semibold" },
                  ]}
                >
                  {item.label}
                </Text>
                <View style={[styles.tableCell, { flex: 1.5 }]}>
                  <Text>{item.value}</Text>
                  {item.status.label && (
                    <Text
                      style={{
                        fontFamily: "Poppins",
                        fontSize: 9,
                        fontWeight: "bold",
                        color: item.status.color,
                        marginTop: 2,
                      }}
                    >
                      {item.status.label}
                    </Text>
                  )}
                </View>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 1.5, fontSize: item.label === "VO2 Max" ? 9 : 11 },
                  ]}
                >
                  {item.range}
                </Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.contentContainer} break>
          {[
            {
              icon: HeartRate,
              title: "SDNN",
              desc: "The standard deviation of all of the NN intervals  (the time intervals between each heartbeat). Higher  numbers usually indicate that your body is coping  better with stress.",
            },
            {
              icon: HeartRate,
              title: "RMSSD",
              desc: "The root mean square of successive intervals be tween normal heartbeats (RMSSD) and is another  measure of heart rate variability ( HRV). RMSSD is  obtained by first calculating each successive time  intervals between heartbeats in msec.",
            },
            {
              icon: PNN50,
              title: "PNN50",
              desc: "PNN50 is a measure of the number of short term fluc tuations in the heart rate and is considered to reflect  changes in (HRV)  driven by the sympathetic branch  of the autonomic nervous system, that prepares the  body for stress and physical activity. PNN50 lower  than 3% is indicative of high risk.",
            },
            {
              icon: CardiacOutput,

              title: "Cardiac Output",
              desc: "The amount of blood  the heart pumps through the  circulatory system in a minute. Cardiac output is  generally 4-8 L/min in an rest.",
            },
            {
              icon: MeanArterialPressure,
              title: "Mean Arterial Pressure",
              desc: "MAP is the average arterial pressure throughout one  heart cycle. MAP is an important measurement that  accounts for flow, resistance, and pressure with in your arteries. It allows doctors to evaluate how  well blood flows through your body and whether it's  reaching all your major organs.",
            },
            {
              icon: HeartUtilization,
              title: "Heart Utilization",
              desc: "Heart utilization is the percentage of overall heart us age to its maximum peak heart rate. Generally < 50%  utilization at rest and 51-85% utilization at moderate  intensity is considered as normal.",
            },
            {
              icon: HeartRateMax,
              title: "Heart Rate Max",
              desc: "Maximum heart rate is the highest number of beats your heat can pump per  minute when it's under high stress (physical or otherwise). Knowing your max  HR can help you track your fitness progress and determine your target heart  rate for exercise intensity.",
            },
            {
              icon: HeartRateReserve,
              title: "Heart Rate Reserve",
              desc: "Heart rate reserve is the difference between your maximum (peak) heart rate  and your resting heart rate. The more fit you are, the lower your resting heart  rate will be. When you have a low resting heart rate, your heart rate reserve is  high.",
            },
            {
              icon: TargetHeartRateRange,
              title: "Target Heart Rate Range",
              desc: " To achieve a certain level of exertion, there is a minimum number of heartbeats  required within a specific timeframe.",
            },
            {
              icon: VO2Max,
              title: "VO2Max",
              desc: "VO2 Max is the maximum rate of oxygen consumption measured during incre mental exercise. Maximal oxygen consumption reflects your overall cardiores piratory fitness, and is an excellent way to assess your fitness level.\n A good V02 max for a man between ages 30 and 39 is 41 to 44.9 For women of the same age, a good V02 max is between 31.5 to 35.6",
            },
          ].map(({ title, desc, icon }) => (
            <View
              key={`info-${title.toLowerCase().replaceAll(" ", "-")}`}
              style={{ marginBottom: 10, flexDirection: "row" }}
              wrap={false}
            >
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 6,
                  backgroundColor: "#EAF5FF",
                  border: "1px solid #A7CDF3",
                  justifyContent: "center",
                  alignItems: "center",
                  marginRight: 8,
                }}
              >
                <Image
                  src={icon}
                  style={{
                    width: 20,
                    height: 20,
                  }}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text
                  style={[
                    styles.normalText,
                    { fontWeight: "bold", fontSize: 12, color: "#333" },
                  ]}
                >
                  {title}
                </Text>
                <Text
                  style={[
                    styles.smallText,
                    {
                      fontWeight: "light",
                      textAlign: "justify",
                      marginTop: 2,
                      color: "#666",
                    },
                  ]}
                >
                  {desc}
                </Text>
              </View>
            </View>
          ))}
        </View>

        {/* physiological */}

        <View style={styles.contentContainer} break>
          <View style={styles.bodyVitalsContainer}>
            <View style={styles.textContainer}>
              <Text style={styles.bodyVitalstitle}>
                <Text style={styles.highlight}>Physiological</Text>
              </Text>
              <Text style={styles.bodyVitalSubtitle}>
                *Calculated based on height and weight inputted by you.
              </Text>
            </View>

            <Image src={Physiological} style={styles.bodyVitalsImage} />
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                Physiological
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                Normal Range
              </Text>
            </View>
            {[
              {
                label: "BMI",
                value: `${result.bmi} kg/m²`,
                status: constants["BMI"].ranges.reduce((p, c) =>
                  result.bmi > c.point ? c : p
                ),
                range: "18.5-24.9 kg/m²",
              },
              {
                label: "Blood Volume",
                value: `${result?.bloodvolume}/ml`,
                range: "NA",
              },
              {
                label: "Total Body Water",
                value: `${result?.tbw}/Liters`,
                range: "NA",
              },
              {
                label: "Body Water %",
                value: `${result?.tbwp}/%`,
                range: "NA",
              },
              {
                label: "Total Body Fat",
                value: `${result?.bodyfat}%`,
                range: "NA",
              },
              {
                label: "Body Weight",
                value: `${result?.weight}/kg`,
                range: "NA",
              },
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 2, fontWeight: "semibold" },
                  ]}
                >
                  {item.label}
                </Text>
                <View style={{ flex: 1.5, flexDirection: "column" }}>
                  <Text
                    style={[styles.tableCell, { padding: 0, borderBottom: 0 }]}
                  >
                    {item.value}
                  </Text>
                  {item.status && (
                    <Text
                      style={[
                        styles.smallText,
                        {
                          fontFamily: "Poppins",
                          fontSize: 9,
                          color: item.status.color || "#000",
                          fontWeight: "bold",
                          marginTop: 20,
                        },
                      ]}
                    >
                      {item.status.label || ""}
                    </Text>
                  )}
                </View>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>
                  {item.range}
                </Text>
              </View>
            ))}
          </View>

          <Text style={[styles.largeText, { marginTop: 15, marginBottom: 10 }]}>
            Health Parameters Description
          </Text>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "space-between",
              marginTop: 10,
            }}
          >
            {[
              {
                icon: HeartRate,
                title: "BMI",
                desc: "Body Mass Index (BMI) is a measurement based on your weight and height. It is more of an indicator than a direct measurement of your total body fat.",
              },
              {
                icon: TotalBodyWater,
                title: "Total Body Water",
                desc: "Total Body Water is the total amount of water in the human body. This includes fluids both inside and outside of cells.",
              },
              {
                icon: TotalBodyFat,
                title: "Total Body Fat",
                desc: "Body fat percentage is an indicator of your body composition and indicates the amount of fat you have in your body.",
              },
              {
                icon: BloodVolume,
                title: "Blood Volume",
                desc: "Blood volume refers to the total amount of fluid circulating within the arteries, capillaries, veins, venules, and chambers of the heart at any time.",
              },
              {
                icon: BodyWater,
                title: "Body Water %",
                desc: "Most of the human body is water, with an average of roughly 60%. The amount of water in the body changes slightly with age, sex, and hydration levels.",
              },
              {
                icon: BodyWeight,
                title: "Body Weight",
                desc: "Body weight refers to the total mass of a person's body, which is composed of various tissues, such as bones, muscles, organs, and body fat.",
              },
            ].map(({ title, desc, icon }, index) => (
              <View
                key={index}
                style={{
                  width: "47%",
                  borderRadius: 8,
                  padding: 8,
                  marginBottom: 12,
                  flexDirection: "row",
                  alignItems: "flex-start",
                }}
                wrap={false}
              >
                <View
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 6,
                    backgroundColor: "#EAF5FF",
                    border: "1px solid #A7CDF3",
                    justifyContent: "center",
                    alignItems: "center",
                    marginRight: 8,
                  }}
                >
                  <Image src={icon} style={{ width: 26, height: 26 }} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#1e293b",
                      marginBottom: 3,
                    }}
                  >
                    {title}
                  </Text>
                  <Text
                    style={{
                      fontSize: 9,
                      lineHeight: 1.3,
                      color: "#475569",
                      textAlign: "justify",
                    }}
                  >
                    {desc}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* Stress */}

        <View style={styles.contentContainer} break>
          {/* Blood Biomakers */}

          <View style={[styles.bodyVitalsContainer]}>
            <View style={styles.textContainer}>
              <Text style={styles.bodyVitalstitle}>
                Blood <Text style={styles.highlight}>Biomarkers</Text>
              </Text>
              <Text style={styles.bodyVitalSubtitle}>
                Understand Blood Biomarkers(HbA1c Level).
              </Text>
            </View>

            <Image src={stress} style={styles.bodyVitalsImage} />
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                HbA1c Level
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                Normal Range
              </Text>
            </View>
            {[
              {
                label: "HbA1c",
                value: result.hba1c,
                status:
                  constants["HbA1c"].ranges
                    .slice()
                    .reverse()
                    .find((r) => result.hba1c >= r.point) || {},
                range: "< 5.7%",
              },
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 2, fontWeight: "semibold" },
                  ]}
                >
                  {item.label}
                </Text>
                <View style={{ flex: 1.5, flexDirection: "column" }}>
                  <Text
                    style={[styles.tableCell, { padding: 0, borderBottom: 0 }]}
                  >
                    {item.value}
                  </Text>
                  <Text
                    style={[
                      styles.smallText,
                      {
                        fontFamily: "Poppins",
                        fontSize: 9,
                        color: item.status.color || "#000",
                        fontWeight: "bold",
                        marginTop: 20,
                      },
                    ]}
                  >
                    {item.status.label || ""}
                  </Text>
                </View>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>
                  {item.range}
                </Text>
              </View>
            ))}
          </View>
          <View style={styles.stressBox}>
            <Text style={[styles.largeText, { fontSize: 10 }]}>
              Your HbA1c Level is{" "}
              {constants["HbA1c"].ranges
                .slice()
                .reverse()
                .find((r) => result.hba1c >= r.point)?.label || "Low"}
            </Text>
            <Text style={styles.smallText}>
              When sugar enters the bloodstream, it binds to hemoglobin, this
              process is called glycation. The HBA1C measures the percentage of
              glycated hemoglobin levels in the blood for the last two to three
              months.
            </Text>
          </View>
          {/* stress */}
          <View style={[styles.bodyVitalsContainer, { marginTop: 60 }]}>
            <View style={styles.textContainer}>
              <Text style={styles.bodyVitalstitle}>
                Stress <Text style={styles.highlight}>Level</Text>
              </Text>
              <Text style={styles.bodyVitalSubtitle}>
                *Calculated based on height and weight inputted by you.
              </Text>
            </View>

            <Image src={stress} style={styles.bodyVitalsImage} />
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                Stress Level
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                Normal Range
              </Text>
            </View>
            {[
              {
                label: "Stress Index",
                value: result.stress,
                status:
                  constants["Stress Level"].ranges
                    .slice()
                    .reverse()
                    .find((r) => result.stress >= r.point) || {},
                range: "NA",
              },
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 2, fontWeight: "semibold" },
                  ]}
                >
                  {item.label}
                </Text>
                <View style={{ flex: 1.5, flexDirection: "column" }}>
                  <Text
                    style={[styles.tableCell, { padding: 0, borderBottom: 0 }]}
                  >
                    {item.value}
                  </Text>
                  <Text
                    style={[
                      styles.smallText,
                      {
                        fontFamily: "Poppins",
                        fontSize: 9,
                        color: item.status.color || "#000",
                        fontWeight: "bold",
                        marginTop: 20,
                      },
                    ]}
                  >
                    {item.status.label || ""}
                  </Text>
                </View>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>
                  {item.range}
                </Text>
              </View>
            ))}
          </View>
          <View style={styles.stressBox}>
            <Text style={[styles.largeText, { fontSize: 10 }]}>
              Your Stress Level is{" "}
              {constants["Stress Level"].ranges
                .slice()
                .reverse()
                .find((r) => result.stress >= r.point)?.label || "Low"}
            </Text>
            <Text style={styles.smallText}>
              The body's reaction to a challenge or demand. There are 5 levels
              of stress.
            </Text>
          </View>

          <View style={styles.disclaimerContainer}>
            <Text
              style={[
                styles.largeText,
                { marginTop: 10, marginBottom: 5, fontSize: 10 },
              ]}
            >
              Disclaimer
            </Text>
            <Text style={styles.disclaimerText}>
              For <Text style={styles.boldText}>Investigational Use Only</Text>.
              This scan is not a substitute for the clinical judgment of a
              health care professional. This scan is intended to improve your
              awareness of general wellness. This scan does not diagnose, treat,
              mitigate or prevent any disease, symptom, disorder or abnormal
              physical state. Consult with a healthcare professional or
              emergency services if you believe you may have a medical issue
            </Text>
          </View>
        </View>
        <View fixed style={styles.footer} />
      </Page>
    </Document>
  );
};

const ScoreData = ({ isGood, fev1fvcRatio, pulmoScore }) => {
  return (
    <>
      <div className="score-card px-5 py-5 w-full bg-[#1D1D1D] rounded-2xl relative z-50 mb-6">
        <div className="flex flex-row justify-between items-center mb-3">
          <h4 className="text-xl font-semibold leading-5 from-[#455C8E] via-[#455C8E] to-[#A7D4FF] bg-gradient-to-r bg-clip-text text-transparent">
            Pulmonary Health Score
          </h4>
        </div>

        <p className="text-xxs text-white/70 text-justify mt-1 mb-4">
          {" "}
          Indicates your overall lung function health based on cough analysis,
          breathing pattern, and voice sample combined with your FEV1/FVC ratio.
        </p>

        <div className="progress-col relative mt-14 mb-1">
          <div className="progress bg-gradient-to-l from-[#64BD40] via-[#FFC444] to-[#C81616]">
            <div
              className="progress-bar"
              style={{
                width: `${Math.floor(pulmoScore)}%`,
              }}
            >
              <div className="progress-value">{Math.floor(pulmoScore)}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="circle-blur lg:w-96 lg:h-96 md:w-96 md:h-96 w-80 h-80 mx-auto absolute left-0 right-0 top-20 blur-[90px]" />
      <h1 className="mt-0 text-2xl relative z-50 text-center mb-5">
        {"Assessment indicates "}
        <span
          className="font-bold"
          style={{ color: isGood ? "#59D341" : "#FF2B2B" }}
        >
          {isGood && "no "}presence
        </span>
        {" of obstructive airway disease (OAD)"}
      </h1>
      <div
        className={`mt-4 p-4 relative z-50 rounded-2xl ${
          isGood ? "bg-success/35" : "bg-error/25"
        } text-sm`}
      >
        <h2 className="font-semibold">
          {isGood && "No "}Obstructive Airway Disease (OAD) Risk Found:
        </h2>
        <p className="mt-1 font-light">
          {"Your assessment shows "}
          <span className="font-semibold">{isGood && "no "}risk</span>
          {/* {` of having ${
            fev1fvcRatio < 0.65 ? "COPD" : "Asthma/COPD"
          } at this time.`} */}
          {` of having Asthma/COPD at this time.`}
        </p>
      </div>
      <div className="mt-5 p-6 rounded-[20px] bg-[#1D1D1D]/70 relative z-50">
        <h2 className="font-light text-secondary">
          Predicted FEV1/FVC:{" "}
          <span className="font-semibold text-white">{fev1fvcRatio}</span>
        </h2>
        <h3 className="mt-3 text-xs font-semibold">What is FEV1/FVC Value?</h3>
        <p className="mt-2 text-xxs relative z-50">
          The FEV1/FVC ratio is a key lung function indicator that compares the
          amount of air you can forcefully exhale in one second (FEV1) to the
          total amount of air you can exhale after taking a deep breath (FVC).
          <br />
          It helps assess how well your lungs are working and is commonly used
          to detect airflow obstruction in conditions like Asthma or COPD.
          <br />
          <br />✅ Normal Range: Typically above 70-80% (varies by age)
          <br />
          <br />✅ Lower Values: May indicate obstructive lung disease
        </p>
        <div className="circle-blur w-32 h-32 mx-auto absolute right-0 bottom-0 blur-[50px]"></div>
      </div>
      <div className="mt-6 p-6 rounded-[20px] bg-[#364560] text-wgite relative z-50">
        <h3 className="text-sm font-semibold text-secondary">
          For Investigational Use Only :
        </h3>
        <p className="mt-2 text-xxs">
          Careplix Pulmo is not a medical device and should not be used as a
          substitute for clinical judgment by a health care professional.
          CarePlix Pulmo is intended to Improve your awareness of your lung
          obstruction health and not intended to diagnose, treat, mitigate or
          prevent any disease, symptom, disorder or abnormal physical condition.
          Contact your health-care provider Immediately if you suspect that you
          have a medical problem. Statements regarding clinical data have not
          been evaluated by the US FDA and are only intended for self-awareness
          and general wellness purposes only.
        </p>
      </div>
    </>
  );
};

const AudioSample = ({ title, audioURL, onPoorQuality }) => {
  const waveformContainerRef = useRef(null);
  const { isReady, isPlaying, wavesurfer } = useWavesurfer({
    container: waveformContainerRef,
    url: audioURL,
    height: 40,
    interact: false,
    waveColor: "#fff",
    progressColor: "#808080",
    cursorColor: "#fff",
    cursorWidth: 2,
    barGap: 1,
    barWidth: 2,
    barRadius: 999,
  });
  const [quality, setQuality] = useState("Good");

  useEffect(() => {
    if (wavesurfer) {
      wavesurfer.registerPlugin(
        Spectrogram.create({ height: 60, splitChannels: false })
      );
      wavesurfer.on("decode", (duration) => {
        const decodedData = wavesurfer.getDecodedData();
        if (decodedData) {
          const audioData = decodedData.getChannelData(0);
          const minValue = 0.025;
          const minSilenceDuration = 0.1;
          const mergeDuration = 0.2;
          const scale = duration / audioData.length;
          const silentRegions = [];
          let start = 0;
          let end = 0;
          let isSilent = false;
          for (let i = 0; i < audioData.length; i++) {
            if (audioData[i] < minValue) {
              if (!isSilent) {
                start = i;
                isSilent = true;
              }
            } else if (isSilent) {
              end = i;
              isSilent = false;
              if (scale * (end - start) > minSilenceDuration) {
                silentRegions.push({
                  start: scale * start,
                  end: scale * end,
                });
              }
            }
          }
          const mergedRegions = [];
          let lastRegion = null;
          for (let i = 0; i < silentRegions.length; i++) {
            if (
              lastRegion &&
              silentRegions[i].start - lastRegion.end < mergeDuration
            ) {
              lastRegion.end = silentRegions[i].end;
            } else {
              lastRegion = silentRegions[i];
              mergedRegions.push(lastRegion);
            }
          }
          const regions = [];
          let lastEnd = 0;
          for (let i = 0; i < mergedRegions.length; i++) {
            regions.push({
              start: lastEnd,
              end: mergedRegions[i].start,
            });
            lastEnd = mergedRegions[i].end;
          }
          if (title === "Cough") {
            if (regions.length >= 3 && regions.length <= 6) setQuality("Good");
            else {
              setQuality("Poor");
              onPoorQuality();
            }
          } else if (title === "Voice") {
            if (regions.length !== 1) {
              setQuality("Poor");
              onPoorQuality();
            } else setQuality("Good");
          }
        }
      });
    }
    return () => {
      wavesurfer?.unAll?.();
    };
  }, [title, wavesurfer, onPoorQuality]);

  return (
    <div className="mt-4 space-y-1 bg-white">
      <h2 className="text-primary font-medium">Recorded {title} Sample</h2>
      <div className="px-4 py-4 flex items-center justify-between space-x-4 rounded-[20px] bg-[#1D1D1D] text-secondary">
        <button
          className="h-10 w-10"
          type="button"
          disabled={!isReady}
          onClick={() => {
            wavesurfer?.playPause?.();
          }}
        >
          <span className="sr-only">Play/Pause</span>
          {isPlaying ? <Pause /> : <Play />}
        </button>
        <div
          className="grow border-l-2 border-secondary"
          ref={waveformContainerRef}
        />
      </div>
      {quality === "Poor" && (
        <p className="text-primary text-xxs">
          <span className="text-error">
            We detected that the audio sample might not be accurate.
          </span>
          <br />
          <span>
            The recording doesn't seem to have the expected characteristics.
          </span>
          &nbsp;
          {title === "Voice" ? (
            <span>
              Please ensure that you're using the correct pronunciation and
              holding the sound consistently.
            </span>
          ) : (
            title === "Cough" && (
              <span>
                The recording doesn't seem to have the expected characteristics.
                Please ensure you're coughing naturally and directly into the
                microphone.
              </span>
            )
          )}
        </p>
      )}
    </div>
  );
};

const PulmonaryResult = () => {
  const { state } = useLocation();
  const [audioQualityLevel, setAudioQualityLevel] = useState(3);
  const {
    userData: { fname, lname, dob, gender, height, weight },
  } = useAuth();

  const reportPDF = useMemo(() => {
    const ageDifMs = Date.now() - new Date(dob).getTime();
    const ageDate = new Date(ageDifMs);
    const age = Math.abs(ageDate.getUTCFullYear() - 1970);
    return (
      <PDFReport
        name={`${fname} ${lname}`}
        age={age}
        gender={gender.slice(0, 1).toUpperCase() + gender.slice(1)}
        height={height}
        weight={weight}
        result={{
          timestamp: new Date(
            state?.scanResult?.entry_time?.replace?.(" ", "T") ?? 0
          ),
          bp_sys: state?.scanResult?.vitals.bp_sys,
          bp_dia: state?.scanResult?.vitals.bp_dia,
          resp_rate: state?.scanResult?.vitals.resp_rate,
          oxy_sat_prcnt: state?.scanResult?.vitals.oxy_sat_prcnt,
          heart_rate: state?.scanResult?.vitals.heart_rate,
          bmi: state?.scanResult?.metadata.physiological_scores.bmi,
          sdnn:
            state?.scanResult?.metadata.heart_scores.sdnn !== "--"
              ? Math.round(state?.scanResult?.metadata.heart_scores.sdnn)
              : "--",
          rmssd:
            state?.scanResult?.metadata.heart_scores.rmssd !== "--"
              ? Math.round(state?.scanResult?.metadata.heart_scores.rmssd)
              : "--",
          pNN50_per: state?.scanResult?.metadata.heart_scores.pNN50_per,
          cardiac_out: state?.scanResult?.metadata.cardiovascular.cardiac_out,
          map: state?.scanResult?.metadata.cardiovascular.map,
          heart_utilized:
            state?.scanResult?.metadata.heart_scores.heart_utilized,
          HRMax: state?.scanResult?.metadata.heart_scores.HRMax,
          HRR: state?.scanResult?.metadata.heart_scores.HRR,
          THRR: state?.scanResult?.metadata.heart_scores.THRR,
          vo2max: state?.scanResult?.metadata.physiological_scores.vo2max,
          bloodvolume:
            state?.scanResult?.metadata.physiological_scores.bloodvolume,
          tbw: state?.scanResult?.metadata.physiological_scores.tbw,
          tbwp: state?.scanResult?.metadata.physiological_scores.tbwp,
          bodyfat: state?.scanResult?.metadata.physiological_scores.bodyfat,
          weight: state?.scanResult?.metadata.physiological_scores.weight,
          stress: state?.scanResult?.metadata.heart_scores.stress_index,
          overallScore: state?.scanResult?.wellness_score,
          pulmoScore: state?.pulmoResult.pulmoScore,
          prq: state?.pulmoResult.prq,
          fev1fvcRatio: state?.pulmoResult.fev1fvcRatio,
          isGood: state?.pulmoResult.isOADScoreGood,
          hba1c: state?.scanResult?.metadata.glucose_info.hba1c,
        }}
      />
    );
  }, [state, fname, lname, dob, gender, height, weight]);

  return (
    <section className="pt-2">
      <div className="flex items-center m-6">
        <Link
          to={-2}
          className="w-9 h-9 mr-6 bg-[#2D2D2D] rounded-full flex justify-center items-center"
        >
          <img src={BackArrow} alt="back arrow" />
        </Link>
        <h2 className="text-xl font-medium">Assessment Result</h2>
      </div>
      <Tab.Group>
        <Tab.List className="relative z-50 mb-6 px-4 flex justify-center space-x-4">
          <Tab
            className={({ selected }) =>
              `px-5 py-2 rounded-full text-sm font-medium ${
                selected ? "bg-white text-black" : "bg-[#2D2D2D] text-white/70"
              }`
            }
          >
            Cardiovascular Observation
          </Tab>
          <Tab
            className={({ selected }) =>
              `px-5 py-2 rounded-full text-sm font-medium ${
                selected ? "bg-white text-black" : "bg-[#2D2D2D] text-white/70"
              }`
            }
          >
            Pulmonary Observation
          </Tab>
        </Tab.List>
        <Tab.Panels>
          <Tab.Panel>
            <CardiovascularObservationResult result={state?.scanResult} />
          </Tab.Panel>
          <Tab.Panel>
            <div className="px-6 !mb-10 tab-screen">
              <ScoreData
                isGood={state?.pulmoResult.isOADScoreGood}
                fev1fvcRatio={state?.pulmoResult.fev1fvcRatio}
                pulmoScore={state?.pulmoResult.pulmoScore}
              />
            </div>
            <div className=" bg-white px-6 pt-3 pb-20">
              {audioQualityLevel < 3 && (
                <div className="mt-6 p-6 flex items-center space-x-4 rounded-[20px] bg-[#f8b62d]">
                  <svg
                    className="shrink-0 w-8"
                    viewBox="0 0 122.88 111.54"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill="#000"
                      d="M2.35,84.42,45.28,10.2l.17-.27h0A23,23,0,0,1,52.5,2.69,17,17,0,0,1,61.57,0a16.7,16.7,0,0,1,9.11,2.69,22.79,22.79,0,0,1,7,7.26q.19.32.36.63l42.23,73.34.24.44h0a22.48,22.48,0,0,1,2.37,10.19,17.63,17.63,0,0,1-2.17,8.35,15.94,15.94,0,0,1-6.93,6.6c-.19.1-.39.18-.58.26a21.19,21.19,0,0,1-9.11,1.75v0H17.61c-.22,0-.44,0-.65,0a18.07,18.07,0,0,1-6.2-1.15A16.42,16.42,0,0,1,3,104.24a17.53,17.53,0,0,1-3-9.57,23,23,0,0,1,1.57-8.74,7.66,7.66,0,0,1,.77-1.51Z"
                    />
                    <path
                      fill="#f8b62d"
                      d="M9,88.75,52.12,14.16c5.24-8.25,13.54-8.46,18.87,0l42.43,73.69c3.39,6.81,1.71,16-9.33,15.77H17.61C10.35,103.8,5.67,97.43,9,88.75Z"
                    />
                    <path
                      fill="#000"
                      d="M57.57,83.78A5.53,5.53,0,0,1,61,82.2a5.6,5.6,0,0,1,2.4.36,5.7,5.7,0,0,1,2,1.3,5.56,5.56,0,0,1,1.54,5,6.23,6.23,0,0,1-.42,1.35,5.57,5.57,0,0,1-5.22,3.26,5.72,5.72,0,0,1-2.27-.53A5.51,5.51,0,0,1,56.28,90a5.18,5.18,0,0,1-.36-1.27,5.83,5.83,0,0,1-.06-1.31h0a6.53,6.53,0,0,1,.57-2,4.7,4.7,0,0,1,1.14-1.56Zm8.15-10.24c-.19,4.79-8.31,4.8-8.49,0-.82-8.21-2.92-29.34-2.86-37.05.07-2.38,2-3.79,4.56-4.33a12.83,12.83,0,0,1,5,0c2.61.56,4.65,2,4.65,4.44v.24L65.72,73.54Z"
                    />
                  </svg>
                  <p className="text-xxs">
                    This assessment result is generated based on your Face scan
                    {audioQualityLevel === 2
                      ? ", Cough audio sample"
                      : audioQualityLevel === 1
                      ? ", Voice audio sample"
                      : ""}{" "}
                    and questionnaire responses. The{" "}
                    {audioQualityLevel === 2
                      ? "Voice"
                      : audioQualityLevel === 1
                      ? "Cough"
                      : "Voice & Cough"}{" "}
                    sample recording did not meet the required characteristics
                    and was excluded from the overall assessment computation.
                  </p>
                </div>
              )}
              <div className="tab-screen grid lg:grid-cols-2 md:grid-cols-2 grid-cols-1 lg:gap-4 md:gap-4 gap-3">
                <AudioSample
                  title="Voice"
                  audioURL={state?.aVowelAudioB64Data ?? ""}
                  onPoorQuality={() => setAudioQualityLevel((s) => s - 1)}
                />
                <AudioSample
                  title="Cough"
                  audioURL={state?.coughAudioB64Data ?? ""}
                  onPoorQuality={() => setAudioQualityLevel((s) => s - 2)}
                />

                <div className="mt-4 p-6 rounded-[20px] bg-[#1D1D1D] text-white">
                  <h3 className="mb-4 text-lg">Heart Rate</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart
                      data={state?.pulmoResult.hrTimeData.data}
                      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="chart-bar-gradient-hr"
                          x1="0%"
                          y1="50%"
                          x2="100%"
                          y2="50%"
                        >
                          <stop offset="25%" stopColor="#0EA573" />
                          <stop offset="75%" stopColor="#063BC4" />
                        </linearGradient>
                      </defs>
                      <XAxis
                        type="number"
                        dataKey="time"
                        allowDataOverflow={false}
                        domain={[0, 35]}
                        ticks={[5, 10, 15, 20, 25, 30]}
                        tickLine={false}
                        tickFormatter={(v) => `${v} sec`}
                        fontSize={10}
                        stroke="#fff"
                        strokeOpacity={0.75}
                        strokeWidth={1}
                      />
                      <YAxis
                        type="number"
                        dataKey="value"
                        domain={["dataMin - 8", "dataMax + 8"]}
                        tick={false}
                        tickMargin={0}
                        stroke="#fff"
                        strokeOpacity={0.75}
                        strokeWidth={1}
                        width={1}
                      />
                      <Line
                        type="monotone"
                        strokeWidth={6}
                        stroke="url(#chart-bar-gradient-hr)"
                        dataKey="value"
                        dot={false}
                      />
                      <ReferenceLine
                        x={10}
                        isFront={true}
                        stroke="#fff"
                        strokeOpacity={0.5}
                        strokeDasharray="3 3"
                      />
                      <ReferenceLine
                        x={25}
                        isFront={true}
                        stroke="#fff"
                        strokeOpacity={0.5}
                        strokeDasharray="3 3"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <div className="mt-1 flex flex-row justify-between gap-6">
                    <div>
                      <h4 className="text-secondary text-sm font-semibold">
                        min HR:
                      </h4>
                      <p>
                        <span className="font-semibold">
                          {state?.pulmoResult.hrTimeData.data[
                            state?.pulmoResult.hrTimeData.min
                          ]?.value ?? "--"}
                        </span>
                        &nbsp;
                        <span className="text-xs">bpm</span>
                      </p>
                      <p className="text-xxs font-light">
                        ⏱️{" "}
                        {state?.pulmoResult.hrTimeData.data[
                          state?.pulmoResult.hrTimeData.min
                        ]?.time ?? "--"}{" "}
                        seconds
                      </p>
                    </div>
                    <div>
                      <h4 className="text-secondary text-sm font-semibold">
                        max HR:
                      </h4>
                      <p>
                        <span className="font-semibold">
                          {state?.pulmoResult.hrTimeData.data[
                            state?.pulmoResult.hrTimeData.max
                          ]?.value ?? "--"}
                        </span>
                        &nbsp;
                        <span className="text-xs">bpm</span>
                      </p>
                      <p className="text-xxs font-light">
                        ⏱️{" "}
                        {state?.pulmoResult.hrTimeData.data[
                          state?.pulmoResult.hrTimeData.max
                        ]?.time ?? "--"}{" "}
                        seconds
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-6 rounded-[20px] bg-[#1D1D1D] text-white">
                  <h3 className="mb-4 text-lg">Respiration Rate</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart
                      data={state?.pulmoResult.rrTimeData.data}
                      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="chart-bar-gradient-rr"
                          x1="0%"
                          y1="50%"
                          x2="100%"
                          y2="50%"
                        >
                          <stop offset="25%" stopColor="#063BC4" />
                          <stop offset="75%" stopColor="#0EA573" />
                        </linearGradient>
                      </defs>
                      <XAxis
                        type="number"
                        dataKey="time"
                        allowDataOverflow={false}
                        domain={[0, 35]}
                        ticks={[5, 10, 15, 20, 25, 30]}
                        tickLine={false}
                        tickFormatter={(v) => `${v} sec`}
                        fontSize={10}
                        stroke="#fff"
                        strokeOpacity={0.75}
                        strokeWidth={1}
                      />
                      <YAxis
                        type="number"
                        dataKey="value"
                        domain={["dataMin - 2", "dataMax + 2"]}
                        tick={false}
                        tickMargin={0}
                        stroke="#fff"
                        strokeOpacity={0.75}
                        strokeWidth={1}
                        width={1}
                      />
                      <Line
                        type="monotone"
                        strokeWidth={6}
                        stroke="url(#chart-bar-gradient-rr)"
                        dataKey="value"
                        dot={false}
                      />
                      <ReferenceLine
                        x={10}
                        isFront={true}
                        stroke="#fff"
                        strokeOpacity={0.5}
                        strokeDasharray="3 3"
                      />
                      <ReferenceLine
                        x={25}
                        isFront={true}
                        stroke="#fff"
                        strokeOpacity={0.5}
                        strokeDasharray="3 3"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <div className="mt-1 flex flex-row justify-between gap-6">
                    <div>
                      <h4 className="text-secondary text-sm font-semibold">
                        min RR:
                      </h4>
                      <p>
                        <span className="font-semibold">
                          {state?.pulmoResult.rrTimeData.data[
                            state?.pulmoResult.rrTimeData.min
                          ]?.value ?? "--"}
                        </span>
                        &nbsp;
                        <span className="text-xs">breathes/min</span>
                      </p>
                      <p className="text-xxs font-light">
                        ⏱️{" "}
                        {state?.pulmoResult.rrTimeData.data[
                          state?.pulmoResult.rrTimeData.min
                        ]?.time ?? "--"}{" "}
                        seconds
                      </p>
                    </div>
                    <div>
                      <h4 className="text-secondary text-sm font-semibold">
                        max RR:
                      </h4>
                      <p>
                        <span className="font-semibold">
                          {state?.pulmoResult.rrTimeData.data[
                            state?.pulmoResult.rrTimeData.max
                          ]?.value ?? "--"}
                        </span>
                        &nbsp;
                        <span className="text-xs">breathes/min</span>
                      </p>
                      <p className="text-xxs font-light">
                        ⏱️{" "}
                        {state?.pulmoResult.rrTimeData.data[
                          state?.pulmoResult.rrTimeData.max
                        ]?.time ?? "--"}{" "}
                        seconds
                      </p>
                    </div>
                  </div>
                </div>

                <div className="col-span-full">
                  <div className="mt-4 px-6 py-4 flex items-center justify-between space-x-4 rounded-[20px] bg-[#1D1D1D]">
                    <h3 className="text-white font-semibold">
                      Pulse-Respiration Quotient (PRQ)
                    </h3>
                    <p className="text-secondary text-2xl font-semibold">
                      {state?.pulmoResult.prq || "--"}
                    </p>
                  </div>
                  <Link
                    to={-2}
                    className="block mt-8 px-6 py-3 lg:w-1/2 md:w-1/2 w-full mx-auto rounded-full outline-none border border-primary/80 bg-white text-primary text-center text-sm font-light"
                  >
                    Back
                  </Link>
                </div>
              </div>
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
      <PDFDownloadLink
        className="flex items-center justify-between space-x-4 py-3 px-6 rounded-xl bg-black text-white text-sm font-medium"
        document={reportPDF}
        fileName="Unity-Health-Report"
      >
        {({ loading }) => (
          <>
            <span>
              {loading ? "Loading document..." : "Download PDF Report"}
            </span>
            <Download className="flex-shrink-0 h-6 w-6" />{" "}
          </>
        )}
      </PDFDownloadLink>
    </section>
  );
};

export default PulmonaryResult;
